<template>
  <q-table
    :columns="columns"
    :rows="charges"
    row-key="id"
    v-model:pagination="pagination"
    :loading="isLoading"
    v-bind="$attrs"
    binary-state-sort
    @request="pagination = $event.pagination"
  >
    <template v-slot:top>
      <q-input
        v-model="keyword"
        placeholder="Name or file ref no."
        clear-icon="fa fa-times"
        clearable
        :debounce="500"
        style="min-width: 320px"
        autocomplete="no"
        filled
        dense
        @clear="keyword = ''">
        <template v-slot:prepend>
          <q-icon name="fa fa-search" />
        </template>
      </q-input>
    </template>
    <template v-slot:body-cell-description="props">
      <q-td :props="props">
        <router-link
          :to="{name: 'client_profile', params: {clientId: props.row.clientId}}"
          class="link"
          title="View Profile"
        >
          {{ props.value }}
        </router-link>
      </q-td>
    </template>
    <template v-slot:body-cell-subTotal="props">
      <q-td :props="props">
        <money :currency="props.row.currency" :amount="props.value" />
      </q-td>
    </template>
    <template v-slot:body-cell-tax="props">
      <q-td :props="props">
        <money :currency="props.row.currency" :amount="props.value" />
      </q-td>
    </template>
    <template v-slot:body-cell-total="props">
      <q-td :props="props">
        <money :currency="props.row.currency" :amount="props.value" />
      </q-td>
    </template>
    <template v-slot:body-cell-charged="props">
      <q-td :props="props">
        {{ $filters.date(props.value) }}
      </q-td>
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <download-receipt-btn :charge-id="props.row.id" />
      </q-td>
    </template>
  </q-table>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  Charge,
  ChargeFilters,
  QueryResult,
  QTablePagination,
} from '@/type';
import { HasTaskMixin } from '@/mixins';
import repository from './charge-repository';
import { name as clientNameFilter } from '../../client/client-filter';
import Money from '../Money.vue';
import DownloadReceiptBtn from './DownloadReceiptBtn.vue';

interface Data {
  columns: object[],
  pagination: QTablePagination,
  keyword: string,
  charges: Charge[],
  task: Promise<QueryResult>|null,
}

export default defineComponent({
  mixins: [HasTaskMixin],
  components: {
    Money,
    DownloadReceiptBtn,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      columns: [
        {
          name: 'description',
          label: 'Description',
          field: 'description',
          align: 'left',
          sortable: true,
        },
        {
          name: 'subTotal',
          label: 'Subtotal',
          // We're not showing promos/discounts in the table so the sub total
          // is really the discounted sub total. i.e. the amount before tax.
          field: (charge: Charge) => charge.discountedSubTotal,
          align: 'left',
        },
        {
          name: 'tax',
          label: 'Tax',
          field: (charge: Charge) => charge.total - charge.discountedSubTotal,
          align: 'left',
        },
        {
          name: 'total',
          label: 'Total',
          field: 'total',
          align: 'left',
        },
        {
          name: 'charged',
          label: 'Charged',
          field: 'chargedAt',
          align: 'left',
          sortable: true,
        },
        {
          name: 'actions',
          label: 'View/Print',
          align: 'right',
        },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        rowsNumber: 0,
        sortBy: 'charged',
        descending: true,
      },
      keyword: '',
      charges: [],
      task: null,
    };
  },
  computed: {
    offset(): number {
      return (this.pagination.page - 1) * this.pagination.rowsPerPage;
    },
    orderBy(): string|undefined {
      if (!this.pagination.sortBy) return undefined;
      const orderBy: { [col: string]: string } = {
        description: 'charge.description',
        charged: 'charge.chargedAt',
      };
      return orderBy[this.pagination.sortBy];
    },
    filters(): ChargeFilters {
      return {
        keyword: this.keyword,
        orderBy: this.orderBy,
        order: (this.pagination.descending) ? 'desc' : 'asc',
        offset: this.offset,
        limit: this.pagination.rowsPerPage,
      };
    },
  },
  watch: {
    customerId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    filters: 'load',
  },
  methods: {
    load() {
      this.task = repository.findAll(this.customerId, this.filters);
      this.task.then((result: QueryResult) => {
        this.pagination.rowsNumber = result.total;
        this.charges = (result.items as Charge[]);
      });
    },
  },
});
</script>
